import { useEffect, useMemo, useRef, useState } from 'react';
import { useCreditRates } from '../../../features/organizations/hooks';
import type { Scenario } from './types';
import { useSceneHandlers } from './hooks/SceneHandlersContext';
import { SegmentedTabs } from '../../common/SegmentedTabs';
import { NarrationThreeDotMenu } from './sceneItem/NarrationThreeDotMenu';
import {
  NARRATION_PROVIDER_ICON_MASKS,
  NARRATION_PROVIDER_OPTIONS,
  normalizeVoiceAge,
  type NarrationProviderCode,
} from './constants';
import { useVoiceFiltersSupported } from './hooks/useNarrationCapabilities';
import { useScenarioVoiceMap } from './hooks/useScenarioVoiceMap';
import { useScenarioVoiceStatuses } from './hooks/useSceneVoiceStatus';
import { NARRATION_SPEAKER_ID } from '../../../features/scenarios/scriptContract';
import type { VoiceAssignment, VoiceSpeaker } from '../../../features/scenarios/api';
import {
  buildSceneLanguages,
  estimateVoiceLinesCredits,
  scenesNeedingVoice,
  unassignedSpeakerIds,
  usesVoiceLines,
  voiceTargetScenes,
  withScenarioVoiceMap,
} from './voiceLines';
import { SpeakerVoiceRow } from './SpeakerVoiceRow';
import { normalizeDialect } from '../state/dialect';
import { NarrationPlayAllButton, useNarrationPlayAll, type NarrationPlayAllStatus } from './NarrationPlayAllButton';
import { NarrationSplitButton } from './NarrationSplitButton';
import styles from './ScenarioGenerator.module.css';
import own from './NarrationSection.module.css';

function isNarrationProviderCode(value: string): value is NarrationProviderCode {
  return NARRATION_PROVIDER_OPTIONS.some((o) => o.value === value);
}

/**
 * シナリオ末尾 (BGM セクションの直上) の「ナレーション」セクション。
 *
 * 歌モノ以外のすべてのシナリオで、ナレーションは「話者付きの行」を 1 行ずつ読み上げて連結する (voice-lines)。
 * - 行の正本: motion-v2 は各シーンの dialogue_lines 全行。実写は narration 欄 (ナレーター行) と、声だけの登場人物の行
 *   (画面に登場する人物のセリフは動画 AI が発話するので含めない)。振り分けは voiceLines.sceneTtsLines
 * - 展開ボディ: 「音声AIモデル」(全話者のプロバイダ) と、ナレーター + 台本に現れる話者ごとの声 (voice_map を直接編集・読み方も話者ごと)
 * - ヘッダー右の「すべてのナレーションを生成」が各シーンの行を読み上げて連結するジョブ (handleGenerateVoiceLines) を受け付ける
 * - 英語発音: 三点メニューの「英語で発音する (全シーン)」とシーン欄の英語トグルを合わせて scene_languages で送る
 * 対象シーンが 1 つも無いシナリオ・歌モノ (narration = 歌詞で Lyria が歌う) では何も描画しない。
 * シーン単体の生成は、各シーンのナレーション欄を展開した中の「このナレーションだけ生成」に残す。
 */
const UNASSIGNED_SPEAKER_MESSAGE = '声が割り当てられていない話者がいます。声を選んでから生成してください';

/**
 * 折りたたみ時に見せる生成進捗リング。動画 / BGM の再生ボタン (24px の円) と同じ外径。
 * 円周を対象シーン数で等分し、12 時から時計回りに生成済み本数ぶんを紫 (ナレーションの
 * アクセント)、残りをグレーで描く。区切りが見えるよう各区画の間に小さな隙間を置く
 * (1 本だけのときは隙間なしの全周)。
 * playStatus を渡すと (再生できるナレーションがあるとき) 中央に再生 / 一時停止のアイコンを描く。
 */
function NarrationProgressRing({ done, total, playStatus }: { done: number; total: number; playStatus?: NarrationPlayAllStatus }) {
  const size = 24;
  const stroke = 4;
  const r = size / 2 - stroke / 2; // 外径 12px を再生ボタンの円と揃える
  const circumference = 2 * Math.PI * r;
  const segments = Math.max(1, total);
  const gap = segments > 1 ? Math.min(1.2, circumference / segments / 6) : 0; // 区切りの隙間 (円周上の長さ)。細い切れ目に留める
  const segmentLength = circumference / segments;
  const visibleLength = Math.max(0.5, segmentLength - gap);
  const doneClamped = Math.min(segments, Math.max(0, done));
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${total}本中${done}本生成済み`}
      style={{ flexShrink: 0, display: 'block' }}
    >
      {Array.from({ length: segments }, (_, i) => {
        // i 番目の区画: 12 時 (-90deg) を起点に時計回りへ i 区画ぶん回転。隙間は区画の末尾側に置く
        const angle = -90 + (360 / segments) * i;
        return (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={i < doneClamped ? '#a855f7' : 'rgba(245, 241, 230, 0.18)'}
            strokeWidth={stroke}
            strokeDasharray={`${visibleLength} ${circumference}`}
            transform={`rotate(${angle} ${size / 2} ${size / 2})`}
          />
        );
      })}
      {/* 中央 (内径 16px) の再生 / 一時停止アイコン。色は生成済み区画と同じ紫。三角は視覚的な重心を合わせるため少し右へ寄せる */}
      {playStatus === 'playing' ? (
        <>
          <rect x={9} y={8.5} width={2} height={7} rx={1} fill="#a855f7" />
          <rect x={13} y={8.5} width={2} height={7} rx={1} fill="#a855f7" />
        </>
      ) : playStatus ? (
        // BGM の再生ボタン (button_play_orange.svg) と同じく角を丸めた三角: 線幅ぶん内側に頂点を置き、丸い線の結合で角を丸める
        <polygon points="10.4,9.2 15,12 10.4,14.8" fill="#a855f7" stroke="#a855f7" strokeWidth={1.6} strokeLinejoin="round" />
      ) : null}
    </svg>
  );
}

export function NarrationSection({
  scenario,
  bgmIsVocalLead,
}: {
  scenario: Scenario;
  /** アクティブシナリオの採用 BGM が is_vocal_lead (ボーカル主体) か (層 B・§3.6)。
   *  true の間、一括生成ボタンを無効化する。 */
  bgmIsVocalLead?: boolean;
}) {
  const {
    selectedNarrationProvider,
    setSelectedNarrationProvider,
    restoreNarrationProvider,
    recommendedNarrator,
    narratorAccent,
    voicesData,
    voicesLoading,
    narrationEnglishScenes,
    handleGenerateVoiceLines,
    voiceLinesRunning,
    voiceLinesLastResult,
    setLastResult,
  } = useSceneHandlers();
  const { data: creditRatesData } = useCreditRates();
  const voiceFiltersSupported = useVoiceFiltersSupported(selectedNarrationProvider);
  const [expanded, setExpanded] = useState(false);
  const [englishAll, setEnglishAll] = useState(false);

  const enabled = usesVoiceLines(scenario);
  const { data: voiceMapData, isLoading: voiceMapLoading } = useScenarioVoiceMap(scenario.id, enabled);
  const voiceStatuses = useScenarioVoiceStatuses(scenario);
  // 一括再生は折りたたみ時のリング中央と展開時のナレーター行のボタンで同じ再生を共用する
  const playAll = useNarrationPlayAll(scenario);
  // 方言の指定が無いシナリオは、全話者の声の一覧を「地域: 標準語」で絞った状態から始める (Gemini のみ・表示だけ)
  const scenarioHasDialect = !!normalizeDialect(scenario.generation_metadata as Record<string, unknown> | undefined);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  // 「音声AIモデル」の復元: voice-map のナレーターの声 (provider 付き) が届いたら、そのプロバイダに合わせる。
  // シナリオごとに最初の 1 回だけ (利用者が切り替えた後に voice-map が取り直されても戻さない)。localStorage には書かない
  const providerSyncedForRef = useRef<string | null>(null);
  const narratorProvider = voiceMapData
    ? (voiceMapData.voice_map?.[NARRATION_SPEAKER_ID]
      ?? voiceMapData.speakers.find((sp) => sp.speaker_id === NARRATION_SPEAKER_ID || sp.kind === 'narration')?.voice
      ?? null)?.provider ?? null
    : null;
  useEffect(() => {
    if (!voiceMapData || providerSyncedForRef.current === scenario.id) return;
    providerSyncedForRef.current = scenario.id;
    if (!narratorProvider || !isNarrationProviderCode(narratorProvider) || narratorProvider === selectedNarrationProvider) return;
    (restoreNarrationProvider ?? setSelectedNarrationProvider)(narratorProvider);
  }, [voiceMapData, narratorProvider, scenario.id, selectedNarrationProvider, restoreNarrationProvider, setSelectedNarrationProvider]);
  const [voiceMessage, setVoiceMessage] = useState<string | null>(null);
  const [voiceSubmitting, setVoiceSubmitting] = useState(false);
  const voiceTargets = useMemo(() => (enabled ? voiceTargetScenes(scenario) : []), [enabled, scenario]);
  const speakers: VoiceSpeaker[] = voiceMapData?.speakers ?? [];
  const voiceTargetIds = voiceTargets.map((s) => s.id);
  const voiceRunning = voiceLinesRunning?.[scenario.id] ?? null;
  const voiceLastResult = voiceLinesLastResult?.[scenario.id] ?? null;
  const generatedCount = voiceTargets.length - scenesNeedingVoice(scenario, voiceStatuses).length;
  const voiceCredits = estimateVoiceLinesCredits(speakers, voiceTargetIds, creditRatesData?.rates);

  // 層 A (シナリオ既定): 歌モノシナリオでは歌が音声になる (§3.6)。歌詞 (= 各シーンの narration) は
  // Lyria が歌うため TTS のセクションそのものを出さない (歌詞の編集はシーン欄の narration で行い、BGM を再生成する)。
  if (!enabled || voiceTargets.length === 0) return null;

  // 層 B (採用 BGM の属性): ボーカル主体の BGM を採用中は生成しても再生されない。
  const blockedByVocalLeadBgm = !!bgmIsVocalLead;

  const generateVoiceLines = async () => {
    if (!handleGenerateVoiceLines || voiceRunning || voiceSubmitting) return;
    // 今回生成する行の話者に声の無い話者がいれば、送らずに強調する (backend も 400 で断る)
    const unassigned = unassignedSpeakerIds(speakers, voiceTargetIds);
    if (voiceMapData && unassigned.length > 0) {
      setHighlighted(new Set(unassigned));
      setVoiceMessage(UNASSIGNED_SPEAKER_MESSAGE);
      setExpanded(true);
      return;
    }
    setVoiceSubmitting(true);
    setVoiceMessage(null);
    try {
      const sceneLanguages = buildSceneLanguages(
        voiceTargets.map((s) => ({ id: s.id, key: `${scenario.id}-scene-${s.order}` })),
        narrationEnglishScenes,
        englishAll,
      );
      const res = await handleGenerateVoiceLines(scenario, undefined, sceneLanguages ? { sceneLanguages } : undefined);
      if (res.ok) {
        setHighlighted(new Set());
      } else if (res.unassignedSpeakers) {
        setHighlighted(new Set(res.unassignedSpeakers));
        setVoiceMessage(UNASSIGNED_SPEAKER_MESSAGE);
        setExpanded(true);
      } else if (res.message) {
        setVoiceMessage(res.message);
        setExpanded(true);
      }
    } finally {
      setVoiceSubmitting(false);
    }
  };

  const handleSpeakerSaved = (speakerId: string, voiceMap: Record<string, VoiceAssignment>) => {
    setLastResult((prev) => withScenarioVoiceMap(prev, scenario.id, voiceMap));
    if (!highlighted.has(speakerId)) return;
    const next = new Set(highlighted);
    next.delete(speakerId);
    setHighlighted(next);
    if (next.size === 0 && voiceMessage === UNASSIGNED_SPEAKER_MESSAGE) setVoiceMessage(null);
  };

  const headerGenerating = voiceRunning?.kind === 'generate';
  const headerDisabled = !!voiceRunning || voiceSubmitting || !handleGenerateVoiceLines || blockedByVocalLeadBgm;
  const narratorSpeaker = speakers.find((sp) => sp.speaker_id === NARRATION_SPEAKER_ID || sp.kind === 'narration');
  const otherSpeakers = speakers.filter((sp) => sp !== narratorSpeaker);
  const speakerRows = [narratorSpeaker, ...otherSpeakers].filter((sp): sp is VoiceSpeaker => !!sp);
  // 一括再生はナレーター行 (ナレーターがいなければ先頭の話者の行) に置く
  const firstSpeaker = speakerRows[0];
  const assignmentOf = (sp: VoiceSpeaker): VoiceAssignment | null => voiceMapData?.voice_map?.[sp.speaker_id] ?? sp.voice ?? null;

  return (
    <div className={styles.flowStepSection} style={{ marginTop: '24px' }}>
      <div
        className={styles.stepHeader}
        onClick={() => setExpanded((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.stepTitle}>
          <span
            className={styles.expandTriangle}
            style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
          >
            <svg width="12" height="12" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 1l4 3-4 3" /></svg>
          </span>
          {/* 折りたたみ時のみ、BGM の再生ボタンと同じ位置・同じ外径で生成進捗リングを出す */}
          {/* 再生できるナレーションがあれば、リング全体を一括再生ボタンにする (生成前は従来どおり中央は空のまま) */}
          {!expanded && (playAll.playable ? (
            <button
              type="button"
              className={own.ringPlayButton}
              data-testid="narration-ring-play-all"
              onClick={(e) => { e.stopPropagation(); playAll.toggle(); }}
              disabled={headerGenerating || playAll.status === 'loading'}
              data-tooltip={playAll.status === 'playing' ? '一時停止' : 'すべてのナレーションを再生'}
              aria-label={playAll.status === 'playing' ? 'ナレーションを一時停止' : 'すべてのナレーションを再生'}
            >
              <NarrationProgressRing done={generatedCount} total={voiceTargets.length} playStatus={playAll.status} />
            </button>
          ) : (
            <span title={`${voiceTargets.length}本中${generatedCount}本生成済み`} style={{ display: 'inline-flex' }}>
              <NarrationProgressRing done={generatedCount} total={voiceTargets.length} />
            </span>
          ))}
          <span style={{ whiteSpace: 'nowrap' }}>ナレーション</span>
          <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px', whiteSpace: 'nowrap' }}>
            {generatedCount}/{voiceTargets.length} シーン生成済み
          </span>
        </div>
        <div className={styles.bgmHeaderRight}>
          <button
            type="button"
            className={`${styles.narrationGenerateButton} ${headerGenerating ? 'ai-loading-active' : ''}`}
            data-testid="narration-generate-all"
            onClick={(e) => {
              e.stopPropagation();
              if (!headerDisabled) void generateVoiceLines();
            }}
            disabled={headerDisabled}
            data-tooltip={
              headerGenerating ? undefined
                : blockedByVocalLeadBgm ? '採用中の BGM がボーカル主体のため、統合動画ではナレーションは再生されません'
                : '各シーンのナレーションを 1 行ずつ読み上げてつなぎます'
            }
            data-tooltip-delay=""
          >
            {headerGenerating ? 'ナレーション生成中...' : (
              <>
                <span>すべてのナレーションを生成</span>
                <span className={styles.creditBadge}>{voiceCredits ?? '…'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className={`${styles.expandContent} ${expanded ? styles.expanded : styles.collapsed}`}>
        <div className={`${styles.bgmBody} ${own.body}`}>
          {(voiceMessage || (voiceLastResult && voiceLastResult.failedLines.length > 0) || voiceLastResult?.advice) && (
            <div className={own.voiceNotices}>
              {voiceMessage && <span className={styles.motionSyncStaleNotice} data-testid="narration-voice-message">{voiceMessage}</span>}
              {voiceLastResult && voiceLastResult.failedLines.length > 0 && (
                <div className={styles.motionSyncStaleNotice} style={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="narration-voice-failed">
                  <span>ナレーションを生成できなかったシーンがあります</span>
                  {voiceLastResult.failedLines.map((line) => <span key={line}>・{line}</span>)}
                </div>
              )}
              {voiceLastResult?.advice && (
                <span className={styles.motionSyncStaleNotice} data-testid="narration-voice-advice">
                  {`ナレーションがシーンの尺に収まりません: ${voiceLastResult.advice}`}
                </span>
              )}
            </div>
          )}
          {/* BGM セクションの「作曲AIモデル」と同じセグメンテッドタブ (アイコン付き・neutral トーン)。全話者のプロバイダ */}
          <div className={own.field}>
            <span className={styles.bgmInstructionLabel}>音声AIモデル</span>
            <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <SegmentedTabs
                tone="neutral"
                ariaLabel="音声AIモデル"
                options={NARRATION_PROVIDER_OPTIONS.map((o) => ({ ...o, iconMaskUrl: NARRATION_PROVIDER_ICON_MASKS[o.value] }))}
                value={selectedNarrationProvider}
                onChange={setSelectedNarrationProvider}
                disabled={headerGenerating}
              />
              {/* 「英語で発音する」(全シーン) はシーン欄と同じ三点メニューに格納 */}
              <NarrationThreeDotMenu
                narrationEnglish={englishAll}
                setNarrationEnglish={setEnglishAll}
              />
            </div>
          </div>

          {voiceMapLoading && <span className={own.fieldHint}>読み込み中…</span>}
          {speakerRows.map((sp) => {
            const isNarrator = sp === narratorSpeaker;
            return (
              <SpeakerVoiceRow
                key={sp.speaker_id}
                scenarioId={scenario.id}
                speaker={sp}
                assignment={assignmentOf(sp)}
                provider={selectedNarrationProvider}
                voices={voicesData || []}
                voicesLoading={!!voicesLoading}
                highlighted={highlighted.has(sp.speaker_id)}
                disabled={headerGenerating}
                defaultGenderFilter={isNarrator && voiceFiltersSupported ? recommendedNarrator?.gender : undefined}
                defaultAgeFilter={isNarrator && voiceFiltersSupported ? normalizeVoiceAge(recommendedNarrator?.age) : undefined}
                defaultAccentCode={isNarrator ? narratorAccent || undefined : undefined}
                preferStandardAccent={!scenarioHasDialect}
                // 生成済みの各シーンのナレーションをシーン順に続けて再生 (従来どおり読み方の右・音声が無ければ出ない)。
                // 右に分割位置の調整 (ハサミ・まとめて読んだ分割前音声があるときだけ出る)
                trailing={sp === firstSpeaker ? (
                  <>
                    <NarrationPlayAllButton player={playAll} disabled={headerGenerating} />
                    <NarrationSplitButton scenarioId={scenario.id} disabled={headerGenerating} />
                  </>
                ) : undefined}
                onSaved={handleSpeakerSaved}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
